import React, { useState, useCallback, useEffect } from 'react';
import { ArrowBack, Close, CloudUpload } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Checkbox,
    Collapse,
    Container,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
    FormLabel,
    RadioGroup,
    Radio,
    Button,
    Avatar,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    FormHelperText,
} from '@mui/material';
import AttributesTable from './AttributesTable';
import CompetitionRules from './CompetitionRules';
import CustomSnackbar from '../../CustomSnackbar';
import { getCompetitionById, updateCompetition, fetchCompetitionAttributes } from '../../services/Competition';
import CompetitionDateSection from './CompetitionDateSection';
import { getImageUrlAdminToken } from '../../utills/ImagesUtilsAdmin';

const CompetitionEditForm = () => {
    const navigate = useNavigate();
    const { competition_id } = useParams();

    // States
    const [publish, setPublish] = useState('draft');
    const [module, setModule] = useState('');
    const [joiningMethod, setJoiningMethod] = useState('manual');
    const [trackingAttribute, setTrackingAttribute] = useState('');
    const [criteriaFunction, setCriteriaFunction] = useState('lcb_score');
    const [trackingWindow, setTrackingWindow] = useState('');
    const [addParticipantRewards, setAddParticipantRewards] = useState(false);
    const [criteriaFunctionForRewards, setCriteriaFunctionForRewards] = useState('');
    const [trackingWindowForRewards, setTrackingWindowForRewards] = useState('');
    const [noOfParticipants, setNoOfParticipants] = useState(''); // String for controlled input
    const [bannerFile, setBannerFile] = useState(null);
    const [existingBanner, setExistingBanner] = useState(null);
    const [formData, setFormData] = useState({
        competition_name: '',
        competition_start_date: '',
        competition_end_date: '',
        enrollment_start_date: '',
        enrollment_end_date: '',
        competition_description: '',
        competition_prize_details: '',
    });
    const [requiredAttributes, setRequiredAttributes] = useState({});
    console.log('requiredAttributes', requiredAttributes);
    const [competitionRules, setCompetitionRules] = useState([]);
    const [status, setStatus] = useState('');
    const [moduleAttributes, setModuleAttributes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [errors, setErrors] = useState({});

    // Helper: Format ISO date to YYYY-MM-DD
    const formatDate = (isoDate) => (!isoDate ? '' : isoDate.split('T')[0]);

    // Fetch competition on mount
    useEffect(() => {
        const fetchCompetition = async () => {
            try {
                const response = await getCompetitionById(competition_id);
                const competition = response.competition;
                console.log('Competition data:', competition);

                // Basic fields
                setFormData({
                    competition_name: competition.competition_name || '',
                    competition_start_date: formatDate(competition.competition_start_date),
                    competition_end_date: formatDate(competition.competition_end_date),
                    enrollment_start_date: formatDate(competition.enrollment_start_date),
                    enrollment_end_date: formatDate(competition.enrollment_end_date),
                    competition_description: competition.competition_description || '',
                    competition_prize_details: competition.competition_prize_details || '',
                });

                setPublish(competition.status);
                setModule(competition.module || '');
                setJoiningMethod(competition.joining_method || 'manual');
                setTrackingAttribute(competition.tracking_attribute || '');
                setCriteriaFunction(competition.criteria_function || '');
                setTrackingWindow(competition.tracking_window || '');
                setCompetitionRules(JSON.parse(competition.competition_rules || '[]'));
                setExistingBanner(competition.banner || null);
                setRequiredAttributes(JSON.parse(competition.required_attributes || '{}'));

                // Participant Rewards
                if (competition.participant_attributes) {
                    const attrs = JSON.parse(competition.participant_attributes);

                    if (Object.keys(attrs).length > 0) {
                        setAddParticipantRewards(true);
                    } else {
                        setAddParticipantRewards(false);
                    }

                    const cf = attrs.criteria_function;
                    if (!isNaN(cf) && cf !== '' && cf !== null) {
                        setCriteriaFunctionForRewards('count');
                        setNoOfParticipants(String(cf));
                    } else {
                        setCriteriaFunctionForRewards(cf || '');
                        setNoOfParticipants('');
                    }
                    setTrackingWindowForRewards(attrs.tracking_window || '');
                }

                // Fetch module attributes
                if (competition.module) {
                    const attrRes = await fetchCompetitionAttributes(competition.module);
                    setModuleAttributes(attrRes.attributes || []);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching competition:', error);
                setStatus('Failed to load competition data.');
                setLoading(false);
            }
        };

        fetchCompetition();
    }, [competition_id]);

    // Handlers
    const handleModuleChange = useCallback((event) => {
        const selectedModule = event.target.value;
        setModule(selectedModule);
        setRequiredAttributes({});
        if (selectedModule) {
            fetchCompetitionAttributes(selectedModule)
                .then((res) => setModuleAttributes(res.attributes || []))
                .catch((err) => {
                    console.error(err);
                    setStatus('Failed to fetch module attributes.');
                    setModuleAttributes([]);
                });
        } else {
            setModuleAttributes([]);
        }
    }, []);

    const handleTrackingAttributeChange = useCallback((e) => setTrackingAttribute(e.target.value), []);

    const handleFormChange = useCallback(
        (field) => (e) => {
            setFormData((prev) => ({ ...prev, [field]: e.target.value }));
        },
        []
    );

    const handleBannerChange = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setStatus('Banner image must be less than 5MB.');
            return;
        }

        const img = new Image();
        img.onload = () => {
            if (img.width === 1200 && img.height === 300) {
                setBannerFile(file);
                setExistingBanner(null);
            } else {
                setStatus('Banner image must be 1200x300 pixels.');
            }
        };
        img.src = URL.createObjectURL(file);
    }, []);

    const handleRulesChange = useCallback((rules) => {
        setCompetitionRules(Array.isArray(rules) ? rules : []);
    }, []);

    const handleAttributesChange = useCallback(({ required_attributes }) => {
        setRequiredAttributes(required_attributes);
    }, []);

    const handleUpdateCompetition = useCallback(async () => {
        // Required fields
        const requiredFields = [
            { field: formData.competition_name, name: 'Competition Name' },
            { field: formData.competition_start_date, name: 'Competition Start Date' },
            { field: formData.competition_end_date, name: 'Competition End Date' },
            ...(formData.enrollment_start_date || formData.enrollment_end_date
                ? [
                    { field: formData.enrollment_start_date, name: 'Enrollment Start Date' },
                    { field: formData.enrollment_end_date, name: 'Enrollment End Date' },
                ]
                : []),
        ];

        if (addParticipantRewards) {
            requiredFields.push(
                { field: criteriaFunctionForRewards, name: 'Participant Criteria Function' },
                { field: trackingWindowForRewards, name: 'Participant Tracking Window' }
            );
            if (criteriaFunctionForRewards === 'count' && (!noOfParticipants || parseInt(noOfParticipants) < 1)) {
                setStatus('Please enter a valid number of participants (minimum 1).');
                return;
            }
        }

        const missing = requiredFields.filter(({ field }) => !field);
        if (missing.length > 0) {
            setStatus(`Please fill: ${missing.map((m) => m.name).join(', ')}`);
            return;
        }

        // Build payload
        const payload = new FormData();
        payload.append('competition_name', formData.competition_name);
        payload.append('competition_start_date', formData.competition_start_date);
        payload.append('competition_end_date', formData.competition_end_date);
        if (formData.enrollment_start_date) payload.append('enrollment_start_date', formData.enrollment_start_date);
        if (formData.enrollment_end_date) payload.append('enrollment_end_date', formData.enrollment_end_date);
        if (formData.competition_description) payload.append('competition_description', formData.competition_description);
        if (formData.competition_prize_details) payload.append('competition_prize_details', formData.competition_prize_details);

        competitionRules.forEach((rule) => payload.append('competition_rules[]', rule));
        if (module) payload.append('module', module);
        if (Object.keys(requiredAttributes).length > 0) {
            payload.append('required_attributes', JSON.stringify(requiredAttributes));
        }
        payload.append('joining_method', joiningMethod);
        if (trackingAttribute) payload.append('tracking_attribute', trackingAttribute);
        if (criteriaFunction) payload.append('criteria_function', criteriaFunction);
        if (trackingWindow) payload.append('tracking_window', trackingWindow);

        if (addParticipantRewards) {
            const finalCriteria = criteriaFunctionForRewards === 'count' ? noOfParticipants : criteriaFunctionForRewards;
            payload.append(
                'participant_attributes',
                JSON.stringify({
                    criteria_function: finalCriteria,
                    tracking_window: trackingWindowForRewards,
                })
            );
        }

        payload.append('status', publish === 'ongoing' || publish === 'upcoming' ? 'active' : 'draft');
        if (bannerFile) {
            payload.append('banner', bannerFile);
        } else if (existingBanner) {
            payload.append('banner', existingBanner);
        }

        //console.log payload for debugging
        for (let pair of payload.entries()) {
            console.log(pair[0] + ', ' + pair[1]);
        }



        try {
            const res = await updateCompetition(competition_id, payload);
            console.log("Competition updated successfully:", res);
            setStatus(res.message || 'Competition updated successfully!');
            setConfirmDialogOpen(false);
            //wait a moment before navigating
            setTimeout(() => {
                navigate('/competitions');
            }, 1500);
        } catch (error) {
            console.error("Error updating competition:", error);
            setStatus(error.message || 'Failed to update competition.');
            setErrors(error.errors || {});
            setConfirmDialogOpen(false);
        }
    }, [
        competition_id,
        formData,
        competitionRules,
        requiredAttributes,
        module,
        joiningMethod,
        trackingAttribute,
        criteriaFunction,
        trackingWindow,
        addParticipantRewards,
        criteriaFunctionForRewards,
        noOfParticipants,
        trackingWindowForRewards,
        publish,
        bannerFile,
        existingBanner,
        navigate,
    ]);

    const handleConfirmUpdate = () => setConfirmDialogOpen(true);
    const handleCloseDialog = () => setConfirmDialogOpen(false);

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <React.Fragment>
            <Container maxWidth="lg" sx={{ mt: { xs: 3, sm: 3, md: 0 }, mb: { xs: 3, sm: 3, md: 0 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        Edit Competition
                    </Typography>
                    {publish === 'ended' ? (
                        <Typography variant="body1" color="error" fontWeight={600}>
                            This competition has ended and cannot be modified.
                        </Typography>
                    ) : (
                        <FormControl>
                            <FormControlLabel
                                control={<Switch
                                    checked={publish === 'ongoing' || publish === 'upcoming'}
                                    onChange={(e) => setPublish(e.target.checked ? 'ongoing' : 'draft')}
                                />}
                                label={publish === 'ongoing' || publish === 'upcoming' ? 'Active' : 'Draft'}
                            />
                            {errors.status && <FormHelperText error>{errors.status[0]}</FormHelperText>}
                        </FormControl>
                    )}
                </Stack>

                <Typography variant="body2" color="primary" fontWeight={600} sx={{ mb: 2 }}>
                    Fill all the (*) required fields.
                </Typography>

                <Paper elevation={0} sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        {/* Existing Fields */}
                        <Grid size={12}>
                            <TextField
                                required
                                label="Competition Name"
                                placeholder="e.g., Best short film 2025"
                                fullWidth
                                size="small"
                                value={formData.competition_name}
                                onChange={handleFormChange('competition_name')}
                                error={!!errors.competition_name}
                                helperText={errors.competition_name?.[0] || ''}
                            />
                        </Grid>
                        <Grid size={12}>
                            <CompetitionDateSection formData={formData} setFormData={setFormData} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                required
                                label="Description"
                                placeholder="Provide a brief description"
                                fullWidth
                                size="small"
                                multiline
                                rows={4}
                                value={formData.competition_description}
                                onChange={handleFormChange('competition_description')}
                                error={!!errors.competition_description}
                                helperText={errors.competition_description?.[0] || ''}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                required
                                label="Competition Prizes"
                                placeholder="Outline the prizes"
                                fullWidth
                                size="small"
                                multiline
                                rows={4}
                                value={formData.competition_prize_details}
                                onChange={handleFormChange('competition_prize_details')}
                                error={!!errors.competition_prize_details}
                                helperText={errors.competition_prize_details?.[0] || ''}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <CompetitionRules onRulesChange={handleRulesChange} rules={competitionRules} errors={errors} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="body1" fontWeight="bold" mb={1.5}>
                                Competition Banner
                            </Typography>
                            <Box
                                sx={{
                                    border: '2px dashed',
                                    borderColor: bannerFile || existingBanner ? 'primary.main' : 'grey.400',
                                    borderRadius: 2,
                                    p: 3,
                                    textAlign: 'center',
                                    position: 'relative',
                                    '&:hover': { borderColor: 'primary.main' },
                                }}
                            >
                                {!(bannerFile || existingBanner) ? (
                                    <Button component="label" variant="text" sx={{ textTransform: 'none' }}>
                                        <Stack spacing={1} alignItems="center">
                                            <CloudUpload sx={{ fontSize: 40, color: 'text.secondary' }} />
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                Upload Banner Image <span style={{ color: 'red' }}>*</span>
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                1200×300 px, Max 5MB
                                            </Typography>
                                        </Stack>
                                        <input type="file" accept="image/*" hidden onChange={handleBannerChange} />
                                    </Button>
                                ) : (
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <Avatar
                                            variant="rounded"
                                            src={bannerFile ? URL.createObjectURL(bannerFile) : getImageUrlAdminToken(existingBanner)}
                                            alt="Banner"
                                            sx={{ width: { xs: 250, sm: 400 }, height: { xs: 100, sm: 150 }, borderRadius: 2 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setBannerFile(null);
                                                setExistingBanner(null);
                                            }}
                                            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.8)' }}
                                        >
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                                {errors.banner && <FormHelperText error>{errors.banner[0]}</FormHelperText>}
                            </Box>
                        </Grid>

                        <Divider sx={{ width: '100%', my: 2 }} />

                        {/* Joining Method & Module */}
                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth>
                                <FormLabel>Joining Method</FormLabel>
                                <RadioGroup row value={joiningMethod} onChange={(e) => setJoiningMethod(e.target.value)}>
                                    <FormControlLabel value="manual" control={<Radio />} label="Manual" />
                                    <FormControlLabel disabled value="auto" control={<Radio />} label="Auto" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 9 }}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel>Select Module</InputLabel>
                                <Select value={module} onChange={handleModuleChange} label="Select Module">
                                    <MenuItem value=""><em>Select Module</em></MenuItem>
                                    <MenuItem value="Project">Project</MenuItem>
                                    <MenuItem value="Profile" disabled>Profile</MenuItem>
                                    <MenuItem value="Post" disabled>Post</MenuItem>
                                </Select>
                                {errors.module && (
                                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                        {errors.module[0]}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {module && (
                            <Grid size={12}>
                                <AttributesTable
                                    onAttributesChange={handleAttributesChange}
                                    module={module}
                                    moduleAttributes={moduleAttributes}
                                    initialAttributes={requiredAttributes}
                                />
                            </Grid>
                        )}

                        {/* Tracking */}
                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl size="small" fullWidth required>
                                <InputLabel>Tracking Attribute</InputLabel>
                                <Select value={trackingAttribute} onChange={handleTrackingAttributeChange} label="Tracking Attribute">
                                    <MenuItem value="" disabled><em>None</em></MenuItem>
                                    {module === 'Project' && <MenuItem value="Rating">Rating</MenuItem>}
                                    <MenuItem value="Reaction" disabled>Reaction</MenuItem>
                                    <MenuItem value="Comment" disabled>Comment</MenuItem>
                                    <MenuItem value="Share" disabled>Share</MenuItem>
                                </Select>
                                {errors.tracking_attribute && (
                                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                        {errors.tracking_attribute[0]}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <FormControl fullWidth required>
                                <FormLabel>Criteria Function</FormLabel>
                                <RadioGroup row value={criteriaFunction} onChange={(e) => setCriteriaFunction(e.target.value)}>
                                    <FormControlLabel disabled value="sum" control={<Radio />} label="Sum" />
                                    <FormControlLabel disabled value="average" control={<Radio />} label="Average" />
                                    <FormControlLabel value="lcb_score" control={<Radio />} label="LCB Score" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <FormControl fullWidth required>
                                <FormLabel>Tracking Window</FormLabel>
                                <RadioGroup row value={trackingWindow} onChange={(e) => setTrackingWindow(e.target.value)}>
                                    <FormControlLabel value="Forever" control={<Radio />} label="Forever" />
                                    <FormControlLabel value="Competition" control={<Radio />} label="Competition" />
                                    <FormControlLabel value="Registration" control={<Radio />} label="Registration" />
                                </RadioGroup>
                                {errors.tracking_window && (
                                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                        {errors.tracking_window[0]}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Participant Rewards */}
                        <Grid size={12}>
                            <FormControlLabel
                                control={<Checkbox checked={addParticipantRewards} onChange={() => setAddParticipantRewards(!addParticipantRewards)} />}
                                label="Evaluators Reward"
                            />
                        </Grid>

                        <Grid size={12}>
                            <Collapse in={addParticipantRewards} timeout="auto" unmountOnExit>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl fullWidth required>
                                            <FormLabel>Criteria Function</FormLabel>
                                            <RadioGroup
                                                row
                                                value={criteriaFunctionForRewards}
                                                onChange={(e) => {
                                                    setCriteriaFunctionForRewards(e.target.value);
                                                    if (e.target.value !== 'count') setNoOfParticipants('');
                                                }}
                                            >
                                                <FormControlLabel disabled value="max" control={<Radio />} label="Max" />
                                                <FormControlLabel value="count" control={<Radio />} label="Count" />
                                            </RadioGroup>
                                        </FormControl>

                                        {/* Only show when 'count' */}
                                        {criteriaFunctionForRewards === 'count' && (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Count Limit"
                                                value={noOfParticipants}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === '' || /^\d+$/.test(val)) {
                                                        setNoOfParticipants(val);
                                                    }
                                                }}
                                                placeholder="e.g., 5"
                                                type="text"
                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                                sx={{ mt: 2 }}
                                                required
                                                helperText="Enter value (minimum 1)"
                                            />
                                        )}
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl fullWidth required>
                                            <FormLabel>Tracking Window</FormLabel>
                                            <RadioGroup
                                                row
                                                value={trackingWindowForRewards}
                                                onChange={(e) => setTrackingWindowForRewards(e.target.value)}
                                            >
                                                <FormControlLabel value="Forever" control={<Radio />} label="Forever" />
                                                <FormControlLabel value="Competition" control={<Radio />} label="Competition" />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </Grid>

                        <Grid size={12} sx={{ textAlign: 'right' }}>
                            <Button variant="contained" onClick={handleConfirmUpdate} sx={{ mt: 2, textTransform: 'none' }}>
                                Update Competition
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>

            {/* Confirm Dialog */}
            <Dialog open={confirmDialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>Confirm Update</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to update this competition?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleUpdateCompetition} variant="contained" color="primary" sx={{ textTransform: 'none' }}>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {status && (
                <CustomSnackbar
                    open={!!status}
                    message={status}
                    severity={status.includes('success') || status.includes('updated') ? 'success' : 'error'}
                    onClose={() => setStatus('')}
                />
            )}
        </React.Fragment>
    );
};

export default CompetitionEditForm;